import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Environment } from '../models/environment.model';
import {
  CountryPoliciesResponse,
  CountryPolicy,
  CountryPolicyStats,
} from '../models/country-policy.model';

/**
 * Country allow/block controls. The API keeps the committed config as the
 * source of truth and records overrides on top, so every response carries both
 * the effective list and the repo default.
 */
@Injectable({ providedIn: 'root' })
export class CountryPoliciesService {
  constructor(
    private httpClient: HttpClient,
    @Inject('environment') private environment: Environment,
  ) {}

  getPolicies() {
    return this.httpClient.get<CountryPoliciesResponse>(
      `${this.environment.api.url}/admin/country-policies`,
    );
  }

  /**
   * Verification traffic and user counts per country, plus per-prefix
   * attempts. Fetched separately from the policies so a slow aggregate never
   * delays the page's controls.
   */
  getStats(days = 7) {
    return this.httpClient.get<CountryPolicyStats>(
      `${this.environment.api.url}/admin/country-policies/stats`,
      { params: { days } as any },
    );
  }

  /** Replaces the whole list; a partial edit cannot half-apply. */
  savePolicy(key: string, countries: string[], enabled?: boolean) {
    const body: { countries: string[]; enabled?: boolean } = { countries };
    if (enabled !== undefined) {
      body.enabled = enabled;
    }
    return this.httpClient.put<{ policies: CountryPolicy[] }>(
      `${this.environment.api.url}/admin/country-policies/${key}`,
      body,
    );
  }

  /**
   * Saves several controls in one request, so a country set across four lists
   * cannot half-apply. Every change is validated before any is written.
   */
  savePolicies(changes: { policy: string; countries: string[]; enabled?: boolean }[]) {
    return this.httpClient.put<{ policies: CountryPolicy[] }>(
      `${this.environment.api.url}/admin/country-policies`,
      { changes },
    );
  }

  /** Drops the override so the committed list applies again. */
  resetPolicy(key: string) {
    return this.httpClient.delete<{ policies: CountryPolicy[]; reset: boolean }>(
      `${this.environment.api.url}/admin/country-policies/${key}`,
    );
  }
}
